import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import { formatDate, subDays } from "../../utilis/date";
import { AUTH_ROLES } from "../../types/auth.type";
import { getAuditLogs, exportAuditLogs, getAuditFilterOptions } from "../../services/audit.service";
import { ADD_NOTIFICATION } from "../../store/types";
import { store } from "../..";

const LIMIT = 25;

function AuditLogsPage() {
  const navigate = useNavigate();
  const { auth } = useSelector(({ auth }) => ({ auth }));
  const isSuper = auth?.role?.slug === AUTH_ROLES.SUPER;

  const [filters, setFilters] = useState({
    dateFrom: formatDate(subDays(new Date(), 30), "YYYY-MM-DD"),
    dateTo: formatDate(new Date(), "YYYY-MM-DD"),
    actor: "",
    action: "",
    resourceType: "",
    outcome: "",
  });
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [actorOptions, setActorOptions] = useState([]);
  const [actionOptions, setActionOptions] = useState([]);
  const [, setOptionsLoading] = useState(false);

  const fetchFilterOptions = useCallback(async () => {
    if (!isSuper) return;
    setOptionsLoading(true);
    try {
      const res = await getAuditFilterOptions({
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
      });
      setActorOptions(res.actors || []);
      setActionOptions(res.actions || []);
    } catch {
      setActorOptions([]);
      setActionOptions([]);
    } finally {
      setOptionsLoading(false);
    }
  }, [isSuper, filters.dateFrom, filters.dateTo]);

  useEffect(() => {
    if (!isSuper) return;
    fetchFilterOptions();
  }, [isSuper, fetchFilterOptions]);

  const fetchLogs = useCallback(async () => {
    if (!isSuper) return;
    setLoading(true);
    try {
      const params = {
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        actor: filters.actor?.trim() || undefined,
        action: filters.action?.trim() || undefined,
        resourceType: filters.resourceType?.trim() || undefined,
        outcome: filters.outcome || undefined,
        search: search?.trim() || undefined,
        page: page + 1,
        limit: LIMIT,
      };
      const res = await getAuditLogs(params);
      setData(res.data || []);
      setTotal(res.total ?? 0);
    } catch (err) {
      store.dispatch({
        type: ADD_NOTIFICATION,
        payload: {
          label: err?.response?.data?.message || "Failed to load audit logs",
          type: "error",
        },
      });
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [isSuper, filters, search, page]);

  useEffect(() => {
    if (!isSuper) return;
    fetchLogs();
  }, [isSuper, fetchLogs]);

  const handleExport = async () => {
    if (!isSuper) return;
    setExporting(true);
    try {
      const params = {
        dateFrom: filters.dateFrom || undefined,
        dateTo: filters.dateTo || undefined,
        actor: filters.actor?.trim() || undefined,
        action: filters.action?.trim() || undefined,
        resourceType: filters.resourceType?.trim() || undefined,
        outcome: filters.outcome || undefined,
        search: search?.trim() || undefined,
      };
      await exportAuditLogs(params);
      store.dispatch({
        type: ADD_NOTIFICATION,
        payload: { label: "Audit log export started.", type: "success" },
      });
    } catch (err) {
      store.dispatch({
        type: ADD_NOTIFICATION,
        payload: {
          label: err?.response?.data?.message || "Export failed",
          type: "error",
        },
      });
    } finally {
      setExporting(false);
    }
  };

  const handleRowClick = (row) => {
    setSelectedRow(row);
    setDrawerOpen(true);
  };

  if (!isSuper) {
    navigate("/settings/profile", { replace: true });
    return null;
  }

  return (
    <Box sx={{ p: 2, maxWidth: 1600, margin: "0 auto" }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Security – Audit logs
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "flex-end",
          }}
        >
          <TextField
            label="From date"
            type="date"
            size="small"
            value={filters.dateFrom}
            onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <TextField
            label="To date"
            type="date"
            size="small"
            value={filters.dateTo}
            onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="audit-actor-label" shrink>
              Actor
            </InputLabel>
            <Select
              value={filters.actor}
              labelId="audit-actor-label"
              label="Actor"
              onChange={(e) => setFilters((f) => ({ ...f, actor: e.target.value }))}
              displayEmpty
              renderValue={(v) => (v ? (actorOptions.find((o) => o.value === v)?.label ?? v) : "All")}
            >
              <MenuItem value="">All</MenuItem>
              {actorOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="audit-action-label" shrink>
              Action / event type
            </InputLabel>
            <Select
              value={filters.action}
              labelId="audit-action-label"
              label="Action / event type"
              onChange={(e) => setFilters((f) => ({ ...f, action: e.target.value }))}
              displayEmpty
              renderValue={(v) => (v || "All")}
            >
              <MenuItem value="">All</MenuItem>
              {actionOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.value}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Resource type"
            size="small"
            value={filters.resourceType}
            onChange={(e) => setFilters((f) => ({ ...f, resourceType: e.target.value }))}
            placeholder="e.g. patient"
            sx={{ minWidth: 140 }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="audit-outcome-label" shrink>
              Outcome
            </InputLabel>
            <Select
              value={filters.outcome}
              labelId="audit-outcome-label"
              label="Outcome"
              onChange={(e) => setFilters((f) => ({ ...f, outcome: e.target.value }))}
              displayEmpty
              renderValue={(v) => (v === "success" ? "Success" : v === "failure" ? "Failure" : "All")}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="failure">Failure</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search"
            placeholder="eventType, action, resourceId"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "action.active", mr: 0.5 }} />,
            }}
            sx={{ minWidth: 220 }}
          />
          <Button
            variant="outlined"
            startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={exporting}
          >
            Export CSV
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Timestamp</TableCell>
              <TableCell>Actor</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Resource</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>Source IP</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  No audit events found.
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={row._id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(row)}
                >
                  <TableCell>
                    {row.timestamp
                      ? formatDate(row.timestamp, "YYYY-MM-DD HH:mm:ss")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {row.actorDisplayName || row.userId || "System"}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                      {row.eventType || row.action || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 160 }}>
                      {row.resourceType}
                      {row.resourceId ? ` · ${row.resourceId}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.outcome}
                      size="small"
                      color={row.outcome === "success" ? "success" : "error"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{row.ipAddress || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={LIMIT}
          rowsPerPageOptions={[LIMIT]}
        />
      </TableContainer>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 400 } } }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Event details</Typography>
            <IconButton onClick={() => setDrawerOpen(false)} aria-label="close">
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedRow && (
            <Box component="dl" sx={{ "& dt": { fontWeight: 600, mt: 1.5, mb: 0.5 }, "& dd": { m: 0 } }}>
              <dt>Timestamp</dt>
              <dd>
                {selectedRow.timestamp
                  ? formatDate(selectedRow.timestamp, "YYYY-MM-DD HH:mm:ss")
                  : "—"}
              </dd>
              <dt>Actor</dt>
              <dd>{selectedRow.actorDisplayName || selectedRow.userId || "System"}</dd>
              {selectedRow.userId && (
                <>
                  <dt>User ID</dt>
                  <dd>{selectedRow.userId}</dd>
                </>
              )}
              <dt>Event type</dt>
              <dd>{selectedRow.eventType || "—"}</dd>
              <dt>Action</dt>
              <dd>{selectedRow.action || "—"}</dd>
              <dt>Resource type</dt>
              <dd>{selectedRow.resourceType || "—"}</dd>
              <dt>Resource ID</dt>
              <dd>{selectedRow.resourceId || "—"}</dd>
              <dt>Outcome</dt>
              <dd>{selectedRow.outcome || "—"}</dd>
              <dt>Source IP</dt>
              <dd>{selectedRow.ipAddress || "—"}</dd>
              <dt>User agent</dt>
              <dd>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {selectedRow.userAgent || "—"}
                </Typography>
              </dd>
              <dt>Request ID</dt>
              <dd>{selectedRow.requestId || "—"}</dd>
              <dt>Metadata</dt>
              <dd>
                {selectedRow.metadata && Object.keys(selectedRow.metadata).length > 0 ? (
                  <Box
                    component="pre"
                    sx={{
                      fontSize: "0.75rem",
                      overflow: "auto",
                      p: 1,
                      bgcolor: "action.hover",
                      borderRadius: 1,
                    }}
                  >
                    {JSON.stringify(selectedRow.metadata, null, 2)}
                  </Box>
                ) : (
                  "—"
                )}
              </dd>
            </Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
}

export default AuditLogsPage;
